import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SaudiRiyalSymbol from '../base/icons/SaudiRiyalSymbol';
import { AreaIcon, BedroomIcon } from '../base/icons/MetricIcons';
import type { Property } from '../../mocks/properties';
import {
  formatNumber,
  getCurrentLocale,
  isPriceOnRequest,
  priceOnRequestLabel,
  resolveImageUrl,
} from '../../utils/propertyUtils';
import { resolveApiBaseUrl } from '../../utils/apiClient';
import { listingBadges } from '../../utils/listingBadges';
import ListingBadges from './ListingBadges';
import { getPropertyDisplayTitle } from '../../utils/arabicPropertyTitle';

const API_BASE_URL = resolveApiBaseUrl();

interface SimilarPropertiesProps {
  property: Property;
  className?: string;
}

interface SimilarItem {
  id: string;
  title: string;
  titleAr?: string;
  price: number;
  bedrooms: number;
  sqft: number;
  city?: string;
  cityAr?: string;
  district?: string;
  imageUrl: string;
  listingType?: string;
  propertyType?: string;
  rentPeriod?: string;
  featured?: boolean;
  viewCount?: number;
  favoriteCount?: number;
  inquiryCount?: number;
  status?: string;
  dateAdded?: string;
  ownerId?: string;
  agentName?: string;
}

/**
 * "عقارات مشابهة" — three compact cards pulled from the listings API (same
 * city, same-agent listings ranked first). Property Page v2 design.
 */
export default function SimilarProperties({ property, className = '' }: SimilarPropertiesProps) {
  const { t, i18n } = useTranslation(['property', 'common']);
  const navigate = useNavigate();
  const locale = getCurrentLocale();
  const isArabic = i18n.language === 'ar';
  const isRTL = i18n.dir() === 'rtl';

  const [items, setItems] = useState<SimilarItem[]>([]);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;

    async function fetchSimilar() {
      try {
        const params = new URLSearchParams({ pageSize: '24', status: 'available' });
        if (property.city) params.set('city', property.city);
        let response = await fetch(`${API_BASE_URL}/properties?${params.toString()}`);
        let list: SimilarItem[] = [];
        if (response.ok) {
          const json = await response.json();
          list = (json.data ?? []) as SimilarItem[];
        }
        // City filter can be too strict (Arabic vs English names) — widen once.
        if (list.filter((p) => String(p.id) !== String(property.id)).length === 0 && property.city) {
          response = await fetch(`${API_BASE_URL}/properties?pageSize=24&status=available`);
          if (response.ok) {
            const json = await response.json();
            list = (json.data ?? []) as SimilarItem[];
          }
        }
        if (cancelled) return;

        // Rank by genuine SIMILARITY (type, district, listing type, price &
        // size proximity) — intentionally NOT by broker, so these are similar
        // properties regardless of who lists them.
        const ranked = list
          .filter((p) => String(p.id) !== String(property.id))
          .map((p) => {
            let score = 0;
            if (p.propertyType === property.propertyType) score += 40;
            if (property.district && p.district && p.district === property.district) score += 30;
            if (p.listingType && property.listingType && p.listingType === property.listingType) score += 25;
            // Price proximity — up to +30 the closer the price is.
            if (property.price > 0 && p.price > 0) {
              const ratio = Math.min(property.price, p.price) / Math.max(property.price, p.price);
              score += Math.round(ratio * 30);
            }
            // Bedroom match.
            if (p.bedrooms === property.bedrooms) score += 15;
            if (p.featured) score += 5;
            return { item: p, score };
          })
          .sort((a, b) => b.score - a.score)
          .slice(0, 3)
          .map(({ item }) => ({ ...item, imageUrl: resolveImageUrl(item.imageUrl) }));

        setItems(ranked);
      } catch {
        if (!cancelled) setItems([]);
      }
    }

    fetchSimilar();
    return () => { cancelled = true; };
  }, [property.id, property.city, property.district, property.listingType, property.propertyType, property.price, property.bedrooms]);

  if (items.length === 0) return null;

  return (
    // A plain section: the property page gives it its hairline and spacing.
    <div className={className}>
      <div className="mb-5 flex items-center justify-between gap-3">
        <h2 className="m-0 text-[1.25rem] font-semibold text-[var(--pv-color-primary)]">
          {t('property.similar.title')}
        </h2>
        <button
          type="button"
          onClick={() => navigate(property.city ? `/search?q=${encodeURIComponent(property.city)}` : '/search')}
          className="flex items-center gap-1 text-sm font-bold text-[#004D40] transition hover:text-[var(--pv-color-text)]"
        >
          {t('property.similar.viewMore')}
          <i className={`${isRTL ? 'ri-arrow-left-line' : 'ri-arrow-right-line'} text-base`} aria-hidden></i>
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => {
          // The same marks as a search card (featured + its strongest earned
          // mark); «نفس الحي» only when it has none of those.
          const hasMarks = listingBadges(item, { earnedLimit: 1 }).length > 0;
          const sameDistrict = !hasMarks && Boolean(property.district && item.district === property.district);
          // Imported listings often have only an English title: on the Arabic page
          // build one from the type and city, as the search cards do.
          const title = getPropertyDisplayTitle(item, isArabic);
          const areaSqm = item.sqft ? Math.round(item.sqft / 10.7639) : 0;
          const onRequest = isPriceOnRequest(item.price);
          const isRent = (item.listingType || '').toUpperCase() === 'RENT';
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(`/property/${item.id}`)}
              className="overflow-hidden rounded-[14px] border border-[var(--pv-border-soft)] bg-white text-start transition hover:shadow-[0_6px_18px_rgba(0,77,64,0.08)]"
            >
              <div className="relative">
                {item.imageUrl && !failedImages.has(item.id) ? (
                  <img
                    src={item.imageUrl}
                    alt={title}
                    loading="lazy"
                    className="block h-[150px] w-full object-cover"
                    onError={() => setFailedImages((prev) => new Set(prev).add(item.id))}
                  />
                ) : (
                  <div className="flex h-[150px] w-full items-center justify-center bg-[var(--pv-color-surface-sunken)]">
                    <i className="ri-building-2-line text-4xl text-[#7AC3B7]" aria-hidden></i>
                  </div>
                )}
                {(hasMarks || sameDistrict) && (
                  <div className="absolute start-2.5 top-2.5 flex flex-wrap gap-1.5">
                    <ListingBadges listing={item} earnedLimit={1} />
                    {sameDistrict && (
                      <span className="inline-flex items-center rounded-full bg-[var(--pv-color-surface)] px-2.5 py-1 text-[0.8125rem] font-semibold leading-none text-[var(--pv-color-text)] ring-1 ring-inset ring-[var(--pv-border-soft)]">
                        {t('property.similar.badges.sameDistrict')}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 px-4 py-3.5">
                <b className="flex items-center gap-1 text-[17px] text-[#004D40]">
                  {!onRequest && <SaudiRiyalSymbol className="h-4 w-4" aria-hidden />}
                  {onRequest ? priceOnRequestLabel(isRTL) : formatNumber(item.price, locale)}
                  {!onRequest && isRent && (
                    <span className="text-xs font-medium text-[#93A7A3]">
                      / {item.rentPeriod === 'monthly' ? t('common:units.month') : t('common:units.year')}
                    </span>
                  )}
                </b>
                <span className="truncate text-[13.5px] font-bold text-[var(--pv-color-text)]">{title}</span>
                <span className="flex items-center gap-3 text-[12.5px] text-[#617672]">
                  {item.bedrooms > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <BedroomIcon className="h-[18px] w-[18px] text-[#004D40]" aria-hidden />
                      {t('property.similar.rooms', { count: item.bedrooms, formatted: formatNumber(item.bedrooms, locale) })}
                    </span>
                  )}
                  {areaSqm > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <AreaIcon className="h-[18px] w-[18px] text-[#004D40]" aria-hidden />
                      {formatNumber(areaSqm, locale)} {isArabic ? 'م²' : 'm²'}
                    </span>
                  )}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
